import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ArrowRight, Bot, FileText, Send, X } from 'lucide-react';
import { KanAIInfoButton } from '../../../components/KanAITransparency';
import { fragaKanai } from '../instruktionerApi';
import './delat.css';

// KanAI svarar i formatet "Svar: / Källor: / Osäkerheter:" (help_ai.py).
// Källorna visas som klickbara chip i stället för som text.
function delaUpp(text) {
  const delar = { Svar: [], Osäkerheter: [] };
  let aktuell = 'Svar';
  String(text || '').split(/\r?\n/).forEach((rad) => {
    const t = rad.trim();
    if (!t) return;
    const rubrik = t.replace(/:$/, '');
    if (/^(Svar|Källor|Osäkerheter):$/.test(t)) { aktuell = rubrik; return; }
    if (aktuell === 'Källor') return;
    (delar[aktuell] || delar.Svar).push(t.replace(/^[•\-*]\s*/, ''));
  });
  return delar;
}

function citerade(text) {
  return new Set(Array.from(String(text || '').matchAll(/\[S(\d+)\]/g)).map((m) => `S${m[1]}`));
}

// KanAI skriver markdown (**fet**, *kursiv*, listor). Bara textformatering
// renderas — inga länkar, bilder eller HTML.
const TILLATNA = ['p', 'strong', 'em', 'ul', 'ol', 'li', 'code', 'br'];
function Markdown({ text }) {
  return (
    <div className="instr-ai-md">
      <ReactMarkdown allowedElements={TILLATNA} unwrapDisallowed skipHtml>
        {String(text || '').replace(/\[S(\d+)\]/g, '[$1]')}
      </ReactMarkdown>
    </div>
  );
}

function Svar({ meddelande, onKalla }) {
  const { Svar: punkter, Osäkerheter: osakert } = delaUpp(meddelande.text);
  const ids = citerade(meddelande.text);
  const kallor = (meddelande.kallor || []).filter((k) => ids.has(k.id));
  const visade = kallor.length ? kallor : (meddelande.kallor || []).slice(0, 2);
  return (
    <div className="instr-ai-msg instr-ai-msg--ai">
      <Markdown text={punkter.length > 1 ? punkter.map((p) => `- ${p}`).join('\n') : (punkter[0] || '')} />
      {osakert.length > 0 && (
        <div className="instr-ai-osakert"><Markdown text={`Att dubbelkolla: ${osakert.join(' ')}`} /></div>
      )}
      {visade.length > 0 && (
        <div className="instr-ai-kallor">
          {visade.map((k) => (
            <button key={k.id} type="button" className="instr-ai-kalla" onClick={() => onKalla(k)}>
              <FileText size={12} strokeWidth={2.5} aria-hidden="true" />
              {k.id.replace('S', '')} · {k.kapitel_nr ? `Kapitel ${k.kapitel_nr} · ${k.rubrik}${k.sida ? ` · s. ${k.sida}` : ''}` : `${k.titel} · ${k.rubrik}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * KanAI om instruktionerna (lila = enbart AI). Underlaget väljs av servern
 * bland instruktioner användaren får se, med `nyckel` som utgångspunkt.
 */
export default function KanAIPanel({ nyckel, beskrivning = null, kontext, valkomst, forslag = [], onKalla, onStang, underrad = 'Svarar utifrån instruktionerna' }) {
  const [meddelanden, setMeddelanden] = useState([]);
  const [fraga, setFraga] = useState('');
  const [laddar, setLaddar] = useState(false);
  const listaRef = useRef(null);

  useEffect(() => { setMeddelanden([]); }, [nyckel, beskrivning && beskrivning.titel]);
  useEffect(() => {
    if (listaRef.current) listaRef.current.scrollTop = listaRef.current.scrollHeight;
  }, [meddelanden, laddar]);

  const skicka = async (text) => {
    const q = (text || fraga).trim();
    if (!q || laddar) return;
    setFraga('');
    setMeddelanden((m) => [...m, { roll: 'anvandare', text: q }]);
    setLaddar(true);
    try {
      const svar = await fragaKanai(q, nyckel, beskrivning && beskrivning.text ? beskrivning : null);
      setMeddelanden((m) => [...m, { roll: 'ai', text: svar.text, kallor: svar.kallor }]);
    } catch (e) {
      setMeddelanden((m) => [...m, { roll: 'ai', text: `Svar:\n• ${e.message || 'KanAI kunde inte svara just nu.'}` }]);
    } finally {
      setLaddar(false);
    }
  };

  return (
    <aside className="instr-ai" aria-label="KanAI">
      <div className="instr-ai-huvud">
        <span className="instr-ai-ikon"><Bot size={18} strokeWidth={2} aria-hidden="true" /></span>
        <div className="instr-ai-namn">
          <div className="instr-ai-titel">KanAI <KanAIInfoButton /></div>
          <div className="instr-ai-underrad">{underrad}</div>
        </div>
        {onStang && (
          <button type="button" className="instr-ikonknapp" onClick={onStang} aria-label="Dölj KanAI">
            <X size={16} strokeWidth={2.5} />
          </button>
        )}
      </div>
      <div className="instr-ai-lista" ref={listaRef}>
        {meddelanden.length === 0 && (
          <div className="instr-ai-valkomst">
            <b>Ställ en fråga</b>
            {valkomst}
            <div className="instr-ai-forslag">
              {forslag.map((f) => (
                <button key={f} type="button" onClick={() => skicka(f)}>
                  <ArrowRight size={13} strokeWidth={2.5} aria-hidden="true" />{f}
                </button>
              ))}
            </div>
          </div>
        )}
        {meddelanden.map((m, i) => (m.roll === 'anvandare'
          ? <div key={i} className="instr-ai-msg instr-ai-msg--du">{m.text}</div>
          : <Svar key={i} meddelande={m} onKalla={onKalla} />))}
        {laddar && <div className="instr-ai-msg instr-ai-msg--ai instr-ai-skriver"><span /><span /><span /></div>}
      </div>
      {kontext && (
        <span className="instr-ai-kontext"><FileText size={12} strokeWidth={2.5} aria-hidden="true" />{kontext}</span>
      )}
      <div className="instr-ai-inmatning">
        <div className="instr-ai-rad">
          <textarea
            rows={2}
            value={fraga}
            onChange={(e) => setFraga(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); skicka(); } }}
            placeholder="Fråga KanAI om instruktionen..."
            aria-label="Fråga KanAI"
          />
          <button type="button" className="instr-ai-skicka" onClick={() => skicka()} disabled={!fraga.trim() || laddar} aria-label="Skicka">
            <Send size={18} strokeWidth={2} />
          </button>
        </div>
        <div className="instr-ai-tips">Enter skickar · Shift+Enter ny rad. Svaren genereras av AI och bör granskas.</div>
      </div>
    </aside>
  );
}
